import { api } from "@utils/api.client"

export type InboxStatus = "NOUVELLE" | "EN_COURS" | "TRAITEE"
export type InboxKind = "CONSULTATION" | "CONTACT" | "RAPPEL"

interface BaseInboxRecord {
  _id: string
  status?: InboxStatus
  notes?: string
  createdAt?: string
  updatedAt?: string
}

export interface ConsultationRecord extends BaseInboxRecord {
  firstName: string
  lastName: string
  email: string
  phone: string
  legalEntity?: string
  numBuildings: number
  numUnits: number
  sector: string
  additionalInfo?: string
}

export interface ContactRecord extends BaseInboxRecord {
  inquiryType: string
  firstName: string
  lastName: string
  email: string
  phone: string
  message: string
}

export interface CallbackRecord extends BaseInboxRecord {
  phone: string
  sector: string
  unitRange?: string
  mainIssue?: string
}

export type InboxRecord =
  | (ConsultationRecord & { kind: "CONSULTATION" })
  | (ContactRecord & { kind: "CONTACT" })
  | (CallbackRecord & { kind: "RAPPEL" })

const endpointByKind: Record<InboxKind, string> = {
  CONSULTATION: "/request-contact-form",
  CONTACT: "/contact",
  RAPPEL: "/callback-request",
}

const loadAllConsultations = async () => {
  const limit = 100
  const firstPage = await api.get<{ data: ConsultationRecord[]; total: number }>("/request-contact-form", {
    page: "1",
    limit: String(limit),
  })
  const pageCount = Math.ceil(firstPage.data.total / limit)
  if (pageCount <= 1) return firstPage.data.data

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      api.get<{ data: ConsultationRecord[] }>("/request-contact-form", {
        page: String(index + 2),
        limit: String(limit),
      }),
    ),
  )

  return [firstPage.data.data, ...remainingPages.map((response) => response.data.data)].flat()
}

export const inboxApi = {
  async list(): Promise<InboxRecord[]> {
    const [consultations, contacts, callbacks] = await Promise.all([
      loadAllConsultations(),
      api.get<ContactRecord[]>("/contact"),
      api.get<CallbackRecord[]>("/callback-request"),
    ])

    return [
      ...consultations.map((record) => ({ ...record, kind: "CONSULTATION" as const })),
      ...contacts.data.map((record) => ({ ...record, kind: "CONTACT" as const })),
      ...callbacks.data.map((record) => ({ ...record, kind: "RAPPEL" as const })),
    ].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
  },

  async update(kind: InboxKind, id: string, update: { status: InboxStatus; notes: string }) {
    const response = await api.patch<BaseInboxRecord>(`${endpointByKind[kind]}/${id}`, update)
    return response.data
  },
}
